import { Skeleton, Stack, styled, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { find, get, isString } from 'lodash';
import { useMemo } from 'react';
import { queryClient } from 'src/configs/queryClient';

// Components
import FlagCountry from 'src/components/common/FlagCountry';
import { StyledWhiteBox } from 'src/features/TariffsAndRules/config';
import { EmptyText } from 'src/utils/constants/config';

// Utilities
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  QUERY_KEY_PUBLIC_COUNTRY_TARIFFS,
  QUERY_KEY_PUBLIC_PRODUCT_NATIONAL_CODE_TARIFFS,
} from 'src/services/queries/useQueryPublic';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';

const StyledTable = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderBottom: 'none',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  borderBottom: `1px dashed ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper2,
}));

const Overview = ({ dataNtmMeasures, isFetching }: { dataNtmMeasures?: INtmMeasures[]; isFetching: boolean }) => {
  const { partner, reporter, hs_code, product } = useQueryParams();
  const listProductNationalFromCache = queryClient.getQueryData([
    QUERY_KEY_PUBLIC_PRODUCT_NATIONAL_CODE_TARIFFS,
    reporter,
    hs_code,
  ]) as IResponse<INtmMeasures>;
  const listCountriesTariff = queryClient.getQueryData([QUERY_KEY_PUBLIC_COUNTRY_TARIFFS]) as IResponse<
    ICountryTariffs[]
  >;

  const productNationalCode = useMemo(() => {
    if (getArrayDefault(listProductNationalFromCache?.data).length > 0) {
      return getArrayDefault(listProductNationalFromCache?.data).find(
        (productNation) => productNation.Code === product,
      );
    }
  }, [listProductNationalFromCache?.data]);

  const getCountryByCountryCode = (countryCode: string) => {
    if (!countryCode) {
      return {
        id: '',
        code: '',
        name: '',
        iso2: '',
        iso3: '',
        valid_from: '',
        valid_until: '',
      };
    }
    const country = find(listCountriesTariff.data, (o) => o.code === countryCode);

    if (!country) {
      return {
        id: '',
        code: '',
        name: '',
        iso2: '',
        iso3: '',
        valid_from: '',
        valid_until: '',
      };
    }

    return country;
  };

  const countryReporter = useMemo(() => {
    return getCountryByCountryCode(reporter && isString(reporter) ? reporter : '');
  }, [reporter, listCountriesTariff.data]);

  const countryPartner = useMemo(() => {
    return getCountryByCountryCode(partner && isString(partner) ? partner : '');
  }, [partner, listCountriesTariff.data]);

  if (isFetching) {
    return <SkeletonContent />;
  }

  return (
    <StyledWhiteBox spacing={2}>
      <Typography variant="h6">Regulatory Requirements</Typography>
      <StyledTable aria-label="customized table">
        <TableBody>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                HS Code
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                {hs_code}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                Product Name
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                {product} - {productNationalCode?.Name}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                Exporting Country
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                <FlagCountry code={countryPartner.iso2} width={16} /> {countryPartner.name}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                Destination Country
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                <FlagCountry code={countryReporter.iso2} width={16} /> {countryReporter.name}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                HS revision
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                {get(dataNtmMeasures, '[0].hs_revision', EmptyText)}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                NTM Classification
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                {get(dataNtmMeasures, '[0].ntm_classification', EmptyText)}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          <StyledTableRow>
            <StyledTableCell component="th">
              <Typography variant="subtitle2" color="text.secondary">
                NTM year
              </Typography>
            </StyledTableCell>
            <StyledTableCell>
              <Typography variant="subtitle2" color="text.secondary">
                {get(dataNtmMeasures, '[0].ntm_year', EmptyText)}
              </Typography>
            </StyledTableCell>
          </StyledTableRow>
          {/*<StyledTableRow>*/}
          {/*  <StyledTableCell component="th">*/}
          {/*    <Typography variant="subtitle2" color="text.secondary">*/}
          {/*      Source*/}
          {/*    </Typography>*/}
          {/*  </StyledTableCell>*/}
          {/*  <StyledTableCell>*/}
          {/*    <Typography variant="subtitle2" color="text.secondary">*/}
          {/*      <div dangerouslySetInnerHTML={{ __html: get(dataNtmMeasures, '[0].DataSource', EmptyText) || '' }} />*/}
          {/*    </Typography>*/}
          {/*  </StyledTableCell>*/}
          {/*</StyledTableRow>*/}
        </TableBody>
      </StyledTable>
    </StyledWhiteBox>
  );
};

export default Overview;

const SkeletonContent = () => {
  return (
    <StyledWhiteBox spacing={2}>
      <Skeleton variant="rectangular" animation="wave" width={150} height={30} />
      <Stack>
        <Skeleton animation="wave" variant="rectangular" height={400} />
      </Stack>
    </StyledWhiteBox>
  );
};
